import React, { useEffect, useMemo, useState } from 'react';
import { Typography } from 'antd';
import {
    DollarOutlined,
    CreditCardOutlined,
    QrcodeOutlined,
    BankOutlined,
    WalletOutlined
} from '@ant-design/icons';
import { getImageUrl } from '@tools/utils/imageUtils.tsx';

interface PaymentMethodCardProps {
    id: number;
    name: string;
    imageUrl?: string;
    isSelected: boolean;
    onClick: () => void;
    disabled?: boolean;
}

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
    name,
    imageUrl,
    isSelected,
    onClick,
    disabled = false
}) => {
    const [imageFailed, setImageFailed] = useState(false);
    const resolvedImageUrl = useMemo(
        () => (imageUrl ? getImageUrl(imageUrl).srcDefault : undefined),
        [imageUrl],
    );
    const shouldShowImage = Boolean(resolvedImageUrl) && !imageFailed;

    useEffect(() => {
        setImageFailed(false);
    }, [resolvedImageUrl]);

    // Función para obtener icono fallback según el nombre
    const getFallbackIcon = (methodName: string) => {
        const lowerName = methodName.toLowerCase();
        if (lowerName.includes('efectivo')) return <DollarOutlined />;
        if (lowerName.includes('mercado') || lowerName.includes('mp')) return <WalletOutlined />; // O un icono específico si se tiene
        if (lowerName.includes('qr')) return <QrcodeOutlined />;
        if (lowerName.includes('transferencia') || lowerName.includes('banco')) return <BankOutlined />;
        return <CreditCardOutlined />;
    };

    return (
        <div
            className={`payment-method-card ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && onClick()}
            role="radio"
            aria-checked={isSelected}
            tabIndex={disabled ? -1 : 0}
        >
            <div className="payment-method-icon">
                {shouldShowImage ? (
                    <img
                        src={resolvedImageUrl}
                        alt={name}
                        className="payment-method-img"
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <span className="payment-method-fallback-icon">
                        {getFallbackIcon(name)}
                    </span>
                )}
            </div>
            <Typography.Text className={`payment-method-name`} ellipsis>
                {name}
            </Typography.Text>

            {isSelected && <div className="payment-selection-indicator" />}
        </div>
    );
};
